import { Alert, Autocomplete, Button, Stack, TextField, Typography } from '@mui/material';
import { MinimalCellRule } from 'interfaces/cellRule';
import { MultiplexPanelCellRules } from 'interfaces/study';
import React, { useState } from 'react';
import { getCellTypeSupportError } from 'utils/cellTypeSupport';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import {
  cellRuleRevisionErrorMessage,
  useCreateCellRuleRevisionMutation,
} from 'utils/queryHooks/cellRule/useCellRuleMutations';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import CellRulesDataGrid from '.';
import { cellRuleRevisionSnapshots, cellRulesFromRevision, sameCellRuleRevisionSnapshots } from './cellRuleRevisions';

const markerIdsFromRevision = (revision?: MultiplexPanelCellRules) => {
  const markerIds = [...(revision?.markerIds ?? [])];
  (revision?.rules ?? []).forEach((rule) => {
    Object.keys(rule?.rules ?? {}).forEach((markerId) => {
      if (markerId && !markerIds.includes(markerId)) markerIds.push(markerId);
    });
  });
  return markerIds;
};

const CellRuleRevisionEditor = ({
  studyId,
  scopeId,
  scopeLabel,
  editable,
  allowedMarkerIds,
  markerDisplayNames,
  selectedRevision,
  onDraftingChange,
  onSaved,
}: {
  studyId: string;
  scopeId: string | null;
  scopeLabel: string;
  editable: boolean;
  allowedMarkerIds: string[] | null;
  markerDisplayNames: Record<string, string>;
  selectedRevision?: MultiplexPanelCellRules;
  onDraftingChange?: (drafting: boolean) => void;
  onSaved: (revision: MultiplexPanelCellRules) => void;
}) => {
  const saveRevision = useCreateCellRuleRevisionMutation();
  const { data: cellTypes, isLoading: isLoadingCellTypes, isError: isCellTypesError } = useCellTypeOptions();
  const { mifMarkerTypeOptionsWithoutDeprecated, isLoadingStainTypeOptions } = useStainTypeOptions();
  const [drafting, setDrafting] = useState(false);
  const [markerIds, setMarkerIds] = useState<string[]>([]);
  const [cellTypeIds, setCellTypeIds] = useState<string[]>([]);
  const [rules, setRules] = useState<MinimalCellRule[]>([]);
  const [initialSnapshots, setInitialSnapshots] = useState<ReturnType<typeof cellRuleRevisionSnapshots>>([]);
  const [reuseWarning, setReuseWarning] = useState<string | null>(null);
  const [omittedMarkerIds, setOmittedMarkerIds] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const scopeName = `for ${scopeLabel}`;

  if (!editable) return null;

  const setDraftingState = (next: boolean) => {
    setDrafting(next);
    onDraftingChange?.(next);
  };

  const startDraft = () => {
    const sourceMarkerIds = allowedMarkerIds ?? markerIdsFromRevision(selectedRevision);
    const reused = selectedRevision
      ? cellRulesFromRevision(selectedRevision, sourceMarkerIds)
      : { cellTypeIds: [], cellRules: [], omittedMarkerIds: [], unsupportedReason: null };
    const nextCellTypeIds = reused.unsupportedReason ? [] : reused.cellTypeIds;
    const nextRules = reused.unsupportedReason ? [] : reused.cellRules;
    const nextMarkerIds = allowedMarkerIds ?? sourceMarkerIds;
    setMarkerIds(nextMarkerIds);
    setCellTypeIds(nextCellTypeIds);
    setRules(nextRules);
    setInitialSnapshots(cellRuleRevisionSnapshots(nextCellTypeIds, nextRules, nextMarkerIds));
    setReuseWarning(reused.unsupportedReason);
    setOmittedMarkerIds(reused.omittedMarkerIds);
    setSaveError(null);
    setDraftingState(true);
  };

  const discardDraft = () => {
    setSaveError(null);
    setDraftingState(false);
  };

  const snapshots = cellRuleRevisionSnapshots(cellTypeIds, rules, markerIds);
  const changed = !sameCellRuleRevisionSnapshots(snapshots, initialSnapshots);
  const cellTypeBlockReason = isLoadingCellTypes
    ? 'Loading cell types...'
    : isCellTypesError
    ? 'Cell types could not be loaded. Reopen Cell rules to retry.'
    : getCellTypeSupportError(cellTypeIds, cellTypes);
  const saveBlockReason =
    cellTypeBlockReason ||
    (!markerIds.length
      ? scopeId
        ? 'This panel has no markers for cell rules.'
        : 'Choose the markers for this revision.'
      : !snapshots.length
      ? 'Add at least one marker rule that is not indifferent.'
      : !changed
      ? 'Change a rule before saving a new revision.'
      : null);
  const cannotStart = allowedMarkerIds !== null && allowedMarkerIds.length === 0;

  const saveDraft = () => {
    if (saveBlockReason || saveRevision.isLoading) return;
    setSaveError(null);
    saveRevision.mutate(
      { studyId, panelId: scopeId, rules: snapshots },
      {
        onSuccess: (revision) => {
          setDraftingState(false);
          onSaved(revision);
        },
        onError: (error) => {
          setSaveError(cellRuleRevisionErrorMessage(error));
        },
      }
    );
  };

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {!drafting ? (
        <>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={startDraft}
            disabled={cannotStart}
            sx={{ alignSelf: 'flex-start' }}
          >
            {`Create new revision ${scopeName}`}
          </Button>
          {cannotStart ? (
            <Typography variant="caption" color="text.secondary">
              This panel has no markers for cell rules.
            </Typography>
          ) : null}
        </>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            {scopeId
              ? 'Saving keeps every earlier revision. Identical rules reuse the existing revision instead of adding another.'
              : 'General rules use stain-catalog markers and are not limited to one panel. Identical rules reuse the existing revision.'}
          </Typography>
          {reuseWarning ? <Alert severity="warning">{reuseWarning}</Alert> : null}
          {omittedMarkerIds.length > 0 ? (
            <Alert severity="warning">
              {`This revision uses markers that are not on this panel: ${omittedMarkerIds
                .map((markerId) => markerDisplayNames[markerId] || markerId)
                .join(', ')}. A new revision keeps only this panel's markers.`}
            </Alert>
          ) : null}
          {allowedMarkerIds === null ? (
            <Autocomplete
              multiple
              loading={isLoadingStainTypeOptions}
              options={mifMarkerTypeOptionsWithoutDeprecated}
              getOptionLabel={(option) => option.displayName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={mifMarkerTypeOptionsWithoutDeprecated.filter((option) => markerIds.includes(option.id))}
              onChange={(_event, value) => {
                setSaveError(null);
                setMarkerIds(value.map((option) => option.id));
              }}
              renderInput={(params) => <TextField {...params} label="Markers" />}
            />
          ) : null}
          <CellRulesDataGrid
            studyId={studyId}
            panelId={scopeId || 'general'}
            stainTypeIds={markerIds}
            markerDisplayNames={markerDisplayNames}
            cellTypeIds={cellTypeIds}
            rules={rules}
            editable
            isLoading={false}
            adminTableLayout
            onCellValueChange={(cellTypeId, stainTypeId, ruleValue) => {
              setSaveError(null);
              setRules((current) => {
                const existing = current.some(
                  (rule) => rule.cellTypeId === cellTypeId && rule.stainTypeId === stainTypeId
                );
                if (!existing) return [...current, { cellTypeId, stainTypeId, ruleValue }];
                return current.map((rule) =>
                  rule.cellTypeId === cellTypeId && rule.stainTypeId === stainTypeId ? { ...rule, ruleValue } : rule
                );
              });
            }}
            onAddCellTypes={(nextCellTypeIds) => {
              setSaveError(null);
              setCellTypeIds((current) => [
                ...current,
                ...nextCellTypeIds.filter((cellTypeId) => !current.includes(cellTypeId)),
              ]);
            }}
            onRemoveCellType={(cellTypeId) => {
              setSaveError(null);
              setCellTypeIds((current) => current.filter((id) => id !== cellTypeId));
              setRules((current) => current.filter((rule) => rule.cellTypeId !== cellTypeId));
            }}
          />
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveBlockReason ? (
            <Typography variant="caption" color="text.secondary">
              {saveBlockReason}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={saveDraft}
              disabled={Boolean(saveBlockReason) || saveRevision.isLoading}
            >
              {saveRevision.isLoading ? 'Saving...' : `Save new revision ${scopeName}`}
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={discardDraft}
              disabled={saveRevision.isLoading}
            >
              {`Discard changes ${scopeName}`}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default CellRuleRevisionEditor;
